'use client';

import { useFormValidationState } from '@react-stately/form';
import { useControlledState } from '@react-stately/utils';
import clsx from 'clsx';
import { useMemo, type ReactNode } from 'react';
import type { ForwardedRef } from 'react';
import { mergeProps, useField } from 'react-aria';
import {
  LabelContext as RACLabelContext,
  FieldErrorContext as RACFieldErrorContext,
  Provider as RACProvider,
  TextContext as RACTextContext,
  ToggleButtonGroup as RACToggleButtonGroup,
  type ToggleButtonGroupProps as RACToggleButtonGroupProps,
} from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { useLocalizedMessages } from '../../i18n/index.js';
import { useProductionWarning } from '../../utils/use-production-warning.js';
import { withStyleProps } from '../../with-style-props.js';
import { FormFieldDescription, FormFieldError, FormFieldLabel } from '../internal/form-field/form-field.js';
import { Stack, type StackProps } from '../spacing/stack/stack.js';
import type { CommonProps, FieldProps, Key } from '../types.js';
import { UNSTABLE_VisuallyHidden as VisuallyHidden } from '../visually-hidden/visually-hidden.js';
import { ToggleButtonGroupContext, type ToggleButtonGroupContextValue } from './context.js';

export interface ToggleButtonGroupProps
  extends
    Omit<CommonProps, 'id'>,
    FieldProps<Set<Key>>,
    Pick<
      RACToggleButtonGroupProps,
      'selectedKeys' | 'defaultSelectedKeys' | 'onSelectionChange' | 'selectionMode' | 'isDisabled'
    > {
  /** Toggle buttons belonging to the group. Each button must have a `value` prop defined. */
  children: ReactNode;
  /** Whether the current selection is invalid. */
  isInvalid?: boolean;
  /** Whether a selection is required before form submission. */
  isRequired?: boolean;
  /** Whether the toggle buttons should wrap to the next line when they don't fit in the available space. */
  wrap?: StackProps['wrap'];
}

function ToggleButtonGroup(
  {
    children,
    label,
    description,
    error: errorMessage,
    validate,
    form,
    name,
    isInvalid,
    isRequired,
    selectedKeys: consumerSelectedKeys,
    defaultSelectedKeys: consumerDefaultSelectedKeys,
    onSelectionChange,
    wrap,
    UNSAFE_className,
    UNSAFE_style,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    'aria-details': ariaDetails,
    ...props
  }: ToggleButtonGroupProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  useProductionWarning(() => {
    if (!label && !ariaLabel && !ariaLabelledBy) {
      console.warn('ToggleButtonGroup requires one of label / aria-label / aria-labelledby for accessibility');
    }
  }, [label, ariaLabel, ariaLabelledBy]);

  const labelMessages = useLocalizedMessages('label');

  // State

  const [selectedKeys, setSelectedKeys] = useControlledState(
    consumerSelectedKeys,
    consumerDefaultSelectedKeys ?? [],
    (newSelectedKeys) => {
      onSelectionChange?.(new Set(newSelectedKeys));
      // `validationState` is technically accessed before its declaration, but there's no way to write this in a different order,
      // and this is a callback anyway so it won't be called immediately
      // eslint-disable-next-line react-hooks/immutability
      validationState.commitValidation();
    },
  );

  const selectedSet = useMemo(() => new Set<Key>(selectedKeys), [selectedKeys]);

  // Validation

  const validationState = useFormValidationState({
    value: selectedSet,
    isInvalid,
    isRequired,
    name,
    validate,
    validationBehavior: 'native',
  });

  const { labelProps, fieldProps, descriptionProps, errorMessageProps } = useField({
    'aria-label': isRequired && ariaLabel != null ? `${ariaLabel} ${labelMessages.format('required')}` : ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    'aria-details': ariaDetails,
    description,
    errorMessage: errorMessage || validationState.displayValidation.validationErrors,
    isInvalid: isInvalid || validationState.displayValidation.isInvalid,
    label,
    labelElementType: 'span',
    validate,
    validationBehavior: 'native',
  });

  // Remove props that we don't want from fieldProps
  const { id, ...filteredFieldProps } = fieldProps;

  const toggleButtonGroupContext: ToggleButtonGroupContextValue = {
    form,
    name,
    isInvalid: isInvalid || validationState.displayValidation.isInvalid,
    isRequired: isRequired && selectedSet.size === 0,
    validationState,
  };

  return (
    <RACToggleButtonGroup
      {...mergeProps(props, filteredFieldProps)}
      ref={ref}
      className={clsx('cim-toggle-button-group', UNSAFE_className)}
      style={UNSAFE_style}
      selectedKeys={selectedKeys}
      onSelectionChange={setSelectedKeys}
    >
      <RACProvider
        values={[
          [RACLabelContext, { ...labelProps, elementType: 'span' }],
          [RACTextContext, { slots: { description: descriptionProps, errorMessage: errorMessageProps } }],
          [RACFieldErrorContext, validationState.displayValidation],
          [ToggleButtonGroupContext, toggleButtonGroupContext],
        ]}
      >
        {label && (
          <FormFieldLabel isRequired={isRequired}>
            {label}
            {/*
             * Button elements don't support `aria-required`, so we need to add visually hidden text to indicate that the field is required.
             * See: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Attributes/aria-required#examples:~:text=Note%3A%20If%20the%20field%27s%20label%20already%20contains%20the%20word%20%22required%22%2C%20it%20is%20recommended%20to%20leave%20out%20the%20aria%2Drequired%20attribute.%20This%20avoids%20that%20screen%20readers%20read%20out%20the%20term%20%22required%22%20twice.
             */}
            {isRequired && <VisuallyHidden>{labelMessages.format('required')}</VisuallyHidden>}
          </FormFieldLabel>
        )}
        <Stack gap={8} direction="horizontal" wrap={wrap}>
          {children}
        </Stack>
        <FormFieldError>{errorMessage}</FormFieldError>
        <FormFieldDescription>{description}</FormFieldDescription>
      </RACProvider>
    </RACToggleButtonGroup>
  );
}

/**
 * Allows users to toggle multiple options, with single or multiple selection.
 *
 * See [toggle button usage guidelines](https://ui.cimpress.io/components/toggle-button/).
 */
const _ToggleButtonGroup = withStyleProps(forwardRef(ToggleButtonGroup), 'ToggleButtonGroup');

export { _ToggleButtonGroup as ToggleButtonGroup };
